import { useEffect, useRef, useState } from 'react'
import { ferry } from '../lib/ferry'

interface Result { outcome: string; partySize?: number; resident?: boolean; message?: string; reference?: string }

const VERDICT: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' }> = {
  boarded: { label: 'Board them', tone: 'good' },
  already_boarded: { label: 'Already boarded', tone: 'warn' },
  not_paid: { label: 'Not paid — send to the counter', tone: 'bad' },
  wrong_sailing: { label: 'Wrong sailing', tone: 'bad' },
  cancelled: { label: 'Cancelled booking', tone: 'bad' },
  unknown: { label: 'Not a valid pass', tone: 'bad' },
}

/**
 * The gate.
 *
 * ── The screen is the product ────────────────────────────────────────────────
 *
 * Everything here is sized for someone standing outdoors, holding a phone in one
 * hand, in a queue that is moving. The verdict is enormous and colour-coded, the
 * party size is the second-biggest thing on the screen, and there is nothing
 * else to read. An agent who has to interpret a screen is an agent who stops
 * scanning and starts waving people through.
 *
 * Manual entry is not a fallback for a broken camera — it is a first-class path,
 * because a dead phone in a passenger's pocket is a daily occurrence and the
 * reference exists precisely so it can be read aloud.
 */
export default function Gate({ sailingId }: { sailingId?: string }) {
  const [result, setResult] = useState<Result | null>(null)
  const [manual, setManual] = useState('')
  const [scanning, setScanning] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)

  /*
   * `submit` through a ref.
   *
   * The camera effect calls it, and it is recreated on every render — so
   * listing it as a dependency would tear the stream down and reopen it
   * constantly, and omitting it silently froze `sailingId` at whatever it was
   * on first render. The ref is what makes the short dependency list honest
   * rather than a suppression: the effect depends on `scanning`, and reads the
   * current submit when it needs one.
   */
  const submitRef = useRef<(token: string) => Promise<void>>(async () => {})

  async function submit(token: string) {
    if (!token.trim()) return
    try {
      setResult(await ferry.scan(token.trim(), sailingId))
    } catch (e) {
      setResult({ outcome: 'unknown', message: (e as Error).message })
    }
  }

  /*
   * BarcodeDetector is native on Android Chrome, which is what a gate agent is
   * holding. Where it is missing the camera is simply not offered rather than
   * pulling in a decoder — manual entry already covers that case, and shipping a
   * second scanner path doubles what has to work on a dock.
   */
  useEffect(() => {
    if (!scanning) return
    const Detector = (window as unknown as { BarcodeDetector?: new (o: object) => { detect(s: CanvasImageSource): Promise<{ rawValue: string }[]> } }).BarcodeDetector
    if (!Detector) { setCameraError('This browser cannot scan. Type the reference instead.'); setScanning(false); return }

    let live = true
    const detector = new Detector({ formats: ['qr_code'] })

    void (async () => {
      try {
        stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (video.current) { video.current.srcObject = stream.current; await video.current.play() }
      } catch {
        setCameraError('The camera could not be opened.'); setScanning(false); return
      }

      const tick = async () => {
        if (!live || !video.current) return
        try {
          const found = await detector.detect(video.current)
          // One code at a time. Two passes in frame is a real thing at a gate
          // and boarding the wrong family is worse than asking for another scan.
          if (found.length === 1) { await submitRef.current(found[0]!.rawValue); setScanning(false); return }
        } catch { /* a frame that will not decode is normal, not an error */ }
        if (live) requestAnimationFrame(() => void tick())
      }
      void tick()
    })()

    return () => {
      live = false
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
    }
  }, [scanning])

  submitRef.current = submit

  const verdict = result ? (VERDICT[result.outcome] ?? VERDICT.unknown!) : null

  return (
    <section className="card gate">
      <h1>Gate</h1>

      {verdict && (
        <div className={`verdict verdict-${verdict.tone}`} role="status" aria-live="assertive">
          <strong>{verdict.label}</strong>
          {result?.outcome === 'boarded' && (
            <>
              <span className="count">{result.partySize}</span>
              <em>{result.partySize === 1 ? 'traveller' : 'travellers'}</em>
              {result.resident && <em className="resident-tag">resident</em>}
            </>
          )}
          {result?.reference && <em className="reference">{result.reference}</em>}
          {result?.message && <em>{result.message}</em>}
          <button onClick={() => setResult(null)}>Next</button>
        </div>
      )}

      {!verdict && (
        <>
          {scanning
            ? <video ref={video} className="scanner" muted playsInline />
            : <button className="primary big" onClick={() => { setCameraError(''); setScanning(true) }}>Scan a pass</button>}
          {cameraError && <p className="error">{cameraError}</p>}

          <form onSubmit={(e) => { e.preventDefault(); void submit(manual); setManual('') }}>
            <label>
              <span>Or type the reference</span>
              <input value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())}
                placeholder="FRY-4K2P" spellCheck={false} autoCapitalize="characters" />
            </label>
            <button>Check</button>
          </form>
        </>
      )}
    </section>
  )
}
